import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaUserPlus, FaUsers, FaBell, FaCircle } from 'react-icons/fa';
import { messagesAPI, usersAPI } from '../utils/api';
import { useChat } from '../hooks/useChat';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import QuickPointBar from '../components/QuickPointBar';
import ConversationList from '../components/messages/ConversationList';
import ChatWindow from '../components/messages/ChatWindow';
import NewChatModal from '../components/messages/NewChatModal';
import NewGroupModal from '../components/messages/NewGroupModal';

const Messages = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [searchUsers, setSearchUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('talkinpoint');
  const [totalUnread, setTotalUnread] = useState(0);
  const [typingUsers, setTypingUsers] = useState({});
  const messagesEndRef = useRef(null);
  const selectedConvRef = useRef(null);
  const typingTimerRef = useRef(null);
  const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');

  const { connect, disconnect, connected, onlineUsers, on, emit } = useChat();

  // Keep ref in sync with state for WS callbacks
  useEffect(() => { selectedConvRef.current = selectedConversation; }, [selectedConversation]);

  // Connect WebSocket on mount
  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  // Register WS event handlers
  useEffect(() => {
    on('message', (data) => {
      const conv = selectedConvRef.current;
      // If message is in current conversation, append it
      if (conv && (data.sender_id === conv.id || data.recipient_id === conv.id)) {
        setMessages(prev => {
          if (prev.find(m => m.id === data.id)) return prev;
          return [...prev, {
            id: data.id,
            sender_id: data.sender_id,
            sender_name: data.sender_name,
            content: data.content,
            created_at: data.created_at,
            is_read: true,
            status: 'sent',
            platform: 'talkinpoint',
          }];
        });
        // Auto-mark as read
        emit({ type: 'read', from_user_id: data.sender_id });
      } else {
        // Show notification for messages in other convos
        toast.success(
          <div className="flex items-center space-x-2">
            <FaBell className="text-[#4A90E2]" />
            <div>
              <p className="font-medium text-sm">New message from {data.sender_name || 'Someone'}</p>
              <p className="text-xs text-gray-500 truncate max-w-[200px]">{data.content}</p>
            </div>
          </div>,
          { duration: 4000 }
        );
      }
      // Update conversation list
      updateConversationWithMessage(data);
    });

    on('message_sent', (data) => {
      // Replace temp message with confirmed one
      setMessages(prev => prev.map(m =>
        m.id === data.temp_id ? { ...m, id: data.id, status: 'sent' } : m
      ));
      updateConversationWithMessage(data);
    });

    on('typing', (data) => {
      setTypingUsers(prev => ({ ...prev, [data.user_id]: data.is_typing }));
      if (data.is_typing) {
        setTimeout(() => {
          setTypingUsers(prev => ({ ...prev, [data.user_id]: false }));
        }, 5000);
      }
    });

    on('messages_read', (data) => {
      setMessages(prev => prev.map(m =>
        m.sender_id === currentUser.id && !m.is_read
          ? { ...m, is_read: true, read_at: data.read_at }
          : m
      ));
    });

    on('reaction', (data) => {
      setMessages(prev => prev.map(m =>
        m.id === data.message_id ? { ...m, reactions: data.reactions } : m
      ));
    });
  }, [on, emit, currentUser.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateConversationWithMessage = (data) => {
    const otherUserId = data.sender_id === currentUser.id ? data.recipient_id : data.sender_id;
    setConversations(prev => {
      const exists = prev.find(c => c.id === otherUserId);
      if (exists) {
        return prev.map(c => c.id === otherUserId ? {
          ...c,
          last_message: { content: data.content, created_at: data.created_at },
          unread_count: (selectedConvRef.current?.id === otherUserId) ? 0 : (c.unread_count || 0) + (data.sender_id !== currentUser.id ? 1 : 0),
        } : c).sort((a, b) => {
          const tA = a.last_message ? new Date(a.last_message.created_at) : new Date(0);
          const tB = b.last_message ? new Date(b.last_message.created_at) : new Date(0);
          return tB - tA;
        });
      }
      // New conversation from an unknown user
      return [{
        id: otherUserId,
        type: 'direct',
        platform: 'talkinpoint',
        name: data.sender_name || 'Unknown',
        avatar_url: data.sender_avatar || '',
        last_message: { content: data.content, created_at: data.created_at },
        unread_count: data.sender_id !== currentUser.id ? 1 : 0,
      }, ...prev];
    });
  };

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  }, []);

  // Auto-open chat when navigated from profile with ?startChat=userId
  useEffect(() => {
    const startChatUserId = searchParams.get('startChat');
    if (!startChatUserId || loading) return;

    // Check if conversation already exists
    const existing = conversations.find(c => c.id === startChatUserId);
    if (existing) {
      handleSelectConversation(existing);
      setSearchParams({}, { replace: true });
      return;
    }

    // Fetch user profile and start new conversation
    const openChat = async () => {
      try {
        const res = await usersAPI.getProfile(startChatUserId);
        const u = res.data;
        startNewChat({ id: u.id, full_name: u.full_name, username: u.username, avatar_url: u.avatar_url });
      } catch {
        toast.error('Could not start conversation');
      }
      setSearchParams({}, { replace: true });
    };
    openChat();
  }, [loading, conversations.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversations = async () => {
    try {
      const response = await messagesAPI.getConversations();
      const directChats = response.data.map(conv => ({
        ...conv,
        id: conv.user?.id || conv.id,
        type: 'direct',
        platform: 'talkinpoint',
        name: conv.user?.full_name,
        username: conv.user?.username,
        avatar_url: conv.user?.avatar_url,
        last_seen: conv.last_seen || null,
        show_last_seen: conv.show_last_seen !== false,
        show_online_status: conv.show_online_status !== false,
        unread_count: 0,
      }));
      directChats.sort((a, b) => {
        const tA = a.last_message ? new Date(a.last_message.created_at) : new Date(0);
        const tB = b.last_message ? new Date(b.last_message.created_at) : new Date(0);
        return tB - tA;
      });
      setConversations(directChats);
      setTotalUnread(directChats.reduce((sum, c) => sum + (c.unread_count || 0), 0));
    } catch (error) {
      console.error('Failed to load conversations', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversation) => {
    try {
      const response = await messagesAPI.getMessages(conversation.id);
      const msgs = response.data.map(m => ({ ...m, platform: 'talkinpoint', status: 'sent' }));
      setMessages(msgs);
      // Mark as read via WS
      emit({ type: 'read', from_user_id: conversation.id });
    } catch (error) {
      console.error('Failed to load messages', error);
    }
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    loadMessages(conversation);
    if (conversation.unread_count > 0) {
      setConversations(prev => prev.map(c => c.id === conversation.id ? { ...c, unread_count: 0 } : c));
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    const tempId = `temp-${Date.now()}`;
    const tempMessage = {
      id: tempId,
      sender_id: currentUser.id,
      sender_name: currentUser.full_name,
      content: newMessage,
      created_at: new Date().toISOString(),
      status: 'sending',
      platform: 'talkinpoint',
    };
    setMessages(prev => [...prev, tempMessage]);
    const msgContent = newMessage;
    setNewMessage('');

    if (connected) {
      // Send via WebSocket
      emit({
        type: 'message',
        recipient_id: selectedConversation.id,
        content: msgContent,
        temp_id: tempId,
      });
    } else {
      // Fallback to REST
      try {
        await messagesAPI.send(selectedConversation.id, msgContent);
        setMessages(prev => prev.map(m => m.id === tempId ? { ...m, status: 'sent' } : m));
      } catch (error) {
        toast.error('Failed to send message');
        setMessages(prev => prev.filter(m => m.id !== tempId));
      }
    }
  };

  const handleTyping = useCallback(() => {
    if (!selectedConversation || !connected) return;
    emit({ type: 'typing', recipient_id: selectedConversation.id, is_typing: true });
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      emit({ type: 'typing', recipient_id: selectedConversation.id, is_typing: false });
    }, 2000);
  }, [selectedConversation, connected, emit]);

  const handleReact = useCallback((messageId, emoji) => {
    if (!connected) return;
    emit({ type: 'reaction', message_id: messageId, emoji });
  }, [connected, emit]);

  const handleSearchUsers = async (query) => {
    setSearchQuery(query);
    if (query.trim().length < 2) { setSearchUsers([]); return; }
    try {
      const response = await usersAPI.search(query);
      const tpUsers = response.data.filter(u => u.id !== currentUser.id).map(u => ({ ...u, platform: 'talkinpoint' }));
      setSearchUsers(tpUsers);
    } catch (error) {
      console.error('Search failed', error);
    }
  };

  const startNewChat = (user) => {
    const existingConv = conversations.find(c => c.id === user.id);
    if (existingConv) {
      handleSelectConversation(existingConv);
    } else {
      const newConv = {
        id: user.id, type: 'direct', platform: 'talkinpoint',
        name: user.full_name, username: user.username,
        avatar_url: user.avatar_url, user,
        last_message: null, unread_count: 0,
      };
      setConversations(prev => [newConv, ...prev]);
      handleSelectConversation(newConv);
    }
    setShowNewChat(false);
    setSearchQuery('');
    setSearchUsers([]);
  };

  const toggleUserSelection = (user) => {
    setSelectedUsers(prev => prev.find(u => u.id === user.id) ? prev.filter(u => u.id !== user.id) : [...prev, user]);
  };

  const createGroup = () => {
    if (!groupName.trim()) { toast.error('Please enter a group name'); return; }
    if (selectedUsers.length < 1) { toast.error('Please select at least 1 member'); return; }
    const newGroup = {
      id: `group-${Date.now()}`, type: 'group', platform: 'talkinpoint',
      name: groupName, avatar_url: null,
      members: selectedUsers.map(u => u.id), memberDetails: selectedUsers,
      last_message: null, unread_count: 0,
    };
    setConversations(prev => [newGroup, ...prev]);
    handleSelectConversation(newGroup);
    setShowNewGroup(false);
    setGroupName('');
    setSelectedUsers([]);
    setSearchQuery('');
    setSearchUsers([]);
    toast.success('Group created!');
  };

  const filteredConversations = conversations.filter(conv => {
    if (activeTab === 'calls') return false; // Calls tab shows CallHistoryList
    if (platformFilter === 'talkinpoint' && conv.platform !== 'talkinpoint') return false;
    if (activeTab === 'direct' && conv.type !== 'direct') return false;
    if (activeTab === 'groups' && conv.type !== 'group') return false;
    if (searchQuery && !conv.name?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const diff = Date.now() - date;
    if (diff < 60000) return 'now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Check if selected conversation's user is typing
  const isTyping = selectedConversation ? typingUsers[selectedConversation.id] : false;

  return (
    <div className="min-h-screen bg-white" data-testid="messages-page">
      <div className="grid grid-cols-1 md:grid-cols-12">
        <Sidebar />
        <main className="col-span-12 md:col-span-10">
          <div className="h-screen flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 bg-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <h1 className="text-2xl font-outfit font-bold text-gray-900" data-testid="messages-title">Messages</h1>
                  {connected && (
                    <span className="flex items-center space-x-1 text-xs text-green-500" data-testid="ws-connected-badge">
                      <FaCircle size={6} />
                      <span>Live</span>
                    </span>
                  )}
                  {totalUnread > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full animate-pulse">{totalUnread} new</span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button onClick={() => setShowNewChat(true)} className="p-2 text-gray-600 hover:text-[#4A90E2] hover:bg-gray-100 rounded-full transition-colors" data-testid="new-chat-btn">
                    <FaUserPlus size={18} />
                  </button>
                  <button onClick={() => setShowNewGroup(true)} className="p-2 text-gray-600 hover:text-[#4A90E2] hover:bg-gray-100 rounded-full transition-colors" data-testid="new-group-btn">
                    <FaUsers size={18} />
                  </button>
                </div>
              </div>
            </div>

            {/* QuickPoint Notes */}
            <div className="px-3 pt-2">
              <QuickPointBar />
            </div>

            <div className="flex-1 flex overflow-hidden">
              {/* Conversation list: always show on desktop, on mobile only when no conversation selected */}
              <div className={`${selectedConversation ? 'hidden md:flex' : 'flex'} w-full md:w-80 flex-shrink-0`}>
                <ConversationList
                  loading={loading}
                  filteredConversations={filteredConversations}
                  selectedConversation={selectedConversation}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  platformFilter={platformFilter}
                  setPlatformFilter={setPlatformFilter}
                  onSelectConversation={handleSelectConversation}
                  onNewChat={() => setShowNewChat(true)}
                  formatTime={formatTime}
                  onlineUsers={onlineUsers}
                />
              </div>

              {/* Chat window: always show on desktop, on mobile only when conversation selected */}
              <div className={`${selectedConversation ? 'flex' : 'hidden md:flex'} flex-1 flex-col bg-white`}>
                <ChatWindow
                  selectedConversation={selectedConversation}
                  messages={messages}
                  newMessage={newMessage}
                  setNewMessage={setNewMessage}
                  onSendMessage={handleSendMessage}
                  onBack={() => setSelectedConversation(null)}
                  currentUserId={currentUser.id}
                  messagesEndRef={messagesEndRef}
                  formatTime={formatTime}
                  onNewChat={() => setShowNewChat(true)}
                  onlineUsers={onlineUsers}
                  isTyping={isTyping}
                  onTyping={handleTyping}
                  connected={connected}
                  onReact={handleReact}
                />
              </div>
            </div>
          </div>
        </main>
      </div>

      <NewChatModal
        show={showNewChat}
        onClose={() => setShowNewChat(false)}
        searchQuery={searchQuery}
        onSearch={handleSearchUsers}
        searchUsers={searchUsers}
        onStartChat={startNewChat}
      />

      <NewGroupModal
        show={showNewGroup}
        onClose={() => setShowNewGroup(false)}
        groupName={groupName}
        setGroupName={setGroupName}
        selectedUsers={selectedUsers}
        toggleUserSelection={toggleUserSelection}
        searchQuery={searchQuery}
        onSearch={handleSearchUsers}
        searchUsers={searchUsers}
        onCreateGroup={createGroup}
      />
    </div>
  );
};

export default Messages;
